import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Tabs, Typography, Button, Spin, Descriptions, Tag, Row, Col, Space, message, Popconfirm, Grid, theme } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, UserOutlined, ShopOutlined } from '@ant-design/icons';
import { crmApi } from '../../../api/client';
import { Contractor, ContractorStatus } from '../types/contractor';
import { ContractorContacts } from '../components/ContractorContacts';
import { ContractorPricingTable } from '../components/ContractorPricingTable';
import { ContractorForm } from '../components/ContractorForm';
import { ContractorOverviewStats } from '../components/ContractorOverviewStats';
import ContractorComments from '../components/ContractorComments';
import { ContractorAnalysis } from '../components/ContractorAnalysis';
import { ContractorInteractions } from '../components/ContractorInteractions';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: any) {
        console.error("ContractorDetailsPage Error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <Title level={4} type="danger">Wystąpił błąd podczas wyświetlania strony</Title>
                    <Text type="secondary">{this.state.error?.message}</Text>
                    <br />
                    <Button style={{ marginTop: 16 }} onClick={() => window.location.reload()}>Odśwież stronę</Button>
                </div>
            );
        }

        return this.props.children;
    }
}

const ContractorDetailsInner = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('1');
    const [contractor, setContractor] = useState<Contractor | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    
    const { token } = theme.useToken();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // Sync tab with URL
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab) {
            const tabMap: Record<string, string> = {
                'details': '1',
                'contacts': '2',
                'pricing': '3',
                'comments': '4',
                'analysis': '5',
                'interactions': '6',
            };
            if (tabMap[tab]) {
                setActiveTab(tabMap[tab]);
            }
        }
    }, [searchParams]);

    const fetchContractor = async (silent = false) => {
        if (!id) return;
        if (!silent) setIsLoading(true);
        try {
            const response = await crmApi.getContractor(id);
            setContractor(response.data);
        } catch (error) {
            console.error('Failed to fetch contractor', error);
            message.error('Nie udało się pobrać danych kontrahenta');
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContractor();
    }, [id]);

    const handleUpdate = () => {
        fetchContractor(true);
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            await crmApi.deleteContractor(id);
            message.success('Usunięto kontrahenta');
            navigate('/b2b');
        } catch (error) {
            message.error('Błąd usuwania kontrahenta');
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!contractor) {
        return <div>Nie znaleziono kontrahenta</div>;
    }

    const getStatusColor = (status: ContractorStatus) => {
        switch (status) {
            case ContractorStatus.ACTIVE: return 'success';
            case ContractorStatus.POTENTIAL: return 'processing';
            case ContractorStatus.BLACKLIST: return 'error';
            case ContractorStatus.INACTIVE: return 'default';
            default: return 'default';
        }
    };

    const items = [
        {
            key: '1',
            label: 'Szczegóły',
            children: (
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <ContractorOverviewStats contractorId={contractor.id} />
                    </Col>
                    <Col span={24}>
                        <Card title="Informacje podstawowe" extra={<Tag color={getStatusColor(contractor.status)}>{contractor.status}</Tag>}>
                            <Descriptions 
                                column={{ xs: 1, sm: 1, md: 2 }}
                                layout={isMobile ? "vertical" : "horizontal"}
                                bordered={isMobile}
                                size={isMobile ? "small" : "default"}
                            >
                                <Descriptions.Item label="NIP">{contractor.nip || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Strona WWW">
                                    {contractor.website ? (
                                        <a href={contractor.website.startsWith('http') ? contractor.website : `https://${contractor.website}`} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                                            {contractor.website}
                                        </a>
                                    ) : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Opiekun">
                                    {contractor.accountManager ? (
                                        <Space>
                                            <UserOutlined />
                                            {contractor.accountManager.firstName} {contractor.accountManager.lastName}
                                        </Space>
                                    ) : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="E-mail (firma)">
                                    {contractor.emails && contractor.emails.length > 0 ? (
                                        <Space direction="vertical" size={0}>
                                            {contractor.emails.map(email => <Text key={email} copyable={{ tooltips: false }} style={{ wordBreak: 'break-all' }}>{email}</Text>)}
                                        </Space>
                                    ) : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Telefony (firma)">
                                    {contractor.phones && contractor.phones.length > 0 ? (
                                        <Space direction="vertical" size={0}>
                                            {contractor.phones.map(phone => <Text key={phone} copyable={{ tooltips: false }}>{phone}</Text>)}
                                        </Space>
                                    ) : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Tagi">
                                    {contractor.tags && contractor.tags.length > 0 ? (
                                        <Space size={[0, 8]} wrap>
                                            {contractor.tags.map(tag => <Tag key={tag} style={{ margin: '2px' }}>{tag}</Tag>)}
                                        </Space>
                                    ) : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Warunki płatności">{contractor.paymentTerms || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Data utworzenia">{dayjs(contractor.createdAt).format('YYYY-MM-DD')}</Descriptions.Item>
                            </Descriptions>
                            {contractor.description && (
                                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Opis firmy:</Text>
                                    <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{contractor.description}</div>
                                </div>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card title="Adres do faktury" size="small">
                            {contractor.invoiceAddress ? (
                                <div>
                                    <Text strong>{contractor.invoiceAddress.street}</Text><br />
                                    <Text>{contractor.invoiceAddress.postalCode} {contractor.invoiceAddress.city}</Text><br />
                                    <Text type="secondary">{contractor.invoiceAddress.country}</Text>
                                </div>
                            ) : <Text type="secondary">Brak danych</Text>}
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card title="Adres dostawy" size="small">
                            {contractor.deliveryAddress ? (
                                <div>
                                    <Text strong>{contractor.deliveryAddress.street}</Text><br />
                                    <Text>{contractor.deliveryAddress.postalCode} {contractor.deliveryAddress.city}</Text><br />
                                    <Text type="secondary">{contractor.deliveryAddress.country}</Text>
                                </div>
                            ) : <Text type="secondary">Brak danych</Text>}
                        </Card>
                    </Col>
                    <Col span={24}>
                        <Card title="Uwagi do realizacji" size="small">
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{contractor.fulfillmentNotes || 'Brak uwag'}</Text>
                        </Card>
                    </Col>
                </Row>
            ),
        },
        {
            key: '2',
            label: 'Kontakty',
            children: <ContractorContacts contractor={contractor} onUpdate={handleUpdate} />,
        },
        {
            key: '3',
            label: 'Cennik i Warunki',
            children: <ContractorPricingTable contractor={contractor} onUpdate={handleUpdate} />,
        },
        {
            key: '4',
            label: 'Komentarze',
            children: <ContractorComments contractorId={contractor.id} />,
        },
        {
            key: '5',
            label: 'Analiza',
            children: <ContractorAnalysis contractorId={contractor.id} />,
        },
        {
            key: '6',
            label: 'Historia kontaktów',
            children: <ContractorInteractions contractorId={contractor.id} contacts={contractor.contacts || []} onUpdate={handleUpdate} />,
        },
    ];

    return (
        <div style={{ padding: isMobile ? '12px' : '24px' }}>
            {/* Header */}
            <div style={{ 
                marginBottom: 24, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 16
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/b2b')} />
                    <Title level={isMobile ? 4 : 2} style={{ margin: 0, wordBreak: 'break-word', flex: 1 }}>
                        <ShopOutlined /> {contractor.name}
                    </Title>
                </div>
                <Space wrap style={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                    <Button 
                        type="primary" 
                        icon={<ShopOutlined />} 
                        onClick={() => navigate(`/orders?create=true&contractorId=${contractor.id}`)}
                        style={{ width: isMobile ? 'calc(50% - 8px)' : 'auto' }}
                    >
                        Zamówienie
                    </Button>
                    <Button 
                        icon={<EditOutlined />} 
                        onClick={() => setIsEditModalOpen(true)}
                        style={{ width: isMobile ? 'calc(50% - 8px)' : 'auto' }}
                    >
                        Edytuj
                    </Button>
                    <Popconfirm
                        title="Usunąć kontrahenta?"
                        description="Ta operacja jest nieodwracalna."
                        onConfirm={handleDelete}
                        okText="Tak"
                        cancelText="Nie"
                    >
                        <Button danger icon={<DeleteOutlined />} style={{ width: isMobile ? '100%' : 'auto' }}>
                            Usuń
                        </Button>
                    </Popconfirm>
                </Space>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={items}
            />

            <ContractorForm
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                contractor={contractor}
                onSubmit={() => {
                    setIsEditModalOpen(false);
                    handleUpdate();
                }}
            />
        </div>
    );
};

export const ContractorDetailsPage = () => {
    return (
        <ErrorBoundary>
            <ContractorDetailsInner />
        </ErrorBoundary>
    );
};
